import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDebounceCallback} from "usehooks-ts";
import {saveViewPort} from "@/api/EncounterApi.ts";
import Encounter from "@/encounter/model/Encounter.ts";
import {ViewPort} from "@/encounter/model/ViewPort.ts";
import {useViewPortRealtime} from "@/encounter/viewport/useViewPortRealtime.ts";
import {useBackgroundErrorNotification} from "@/api/useBackgroundErrorNotification.ts";

export function usePlayerViewPortSync(encounter: Encounter, initialViewPort: ViewPort) {
    const {subscribeToUpdates, publishLive: publishLiveUpdate} = useViewPortRealtime(encounter.id);
    const [playerViewPort, setPlayerViewPort] = useState(initialViewPort);
    const latestPlayerViewPort = useRef(initialViewPort);
    const {report, recover} = useBackgroundErrorNotification();

    const publishLive = useCallback((viewPort: ViewPort) => {
        latestPlayerViewPort.current = viewPort;
        setPlayerViewPort(viewPort);
        publishLiveUpdate(viewPort);
    }, [publishLiveUpdate]);

    const persistViewPort = useCallback((viewPort: ViewPort) => {
        latestPlayerViewPort.current = viewPort;
        setPlayerViewPort(viewPort);
        void saveViewPort(encounter, viewPort)
            .then(recover)
            .catch(error => report(error, "Die Spieleransicht konnte nicht gespeichert werden."));
    }, [encounter, recover, report]);

    const throttledPublishViewPort = useDebounceCallback(publishLive, 33, {
        leading: true,
        trailing: true,
        maxWait: 33,
    });
    const debouncedPersistViewPort = useDebounceCallback(persistViewPort, 200);

    const scheduleViewPortUpdate = useCallback((viewPort: ViewPort) => {
        throttledPublishViewPort(viewPort);
        debouncedPersistViewPort(viewPort);
    }, [debouncedPersistViewPort, throttledPublishViewPort]);

    const cancelScheduledUpdates = useCallback(() => {
        throttledPublishViewPort.cancel();
        debouncedPersistViewPort.cancel();
    }, [debouncedPersistViewPort, throttledPublishViewPort]);

    const getPlayerViewPort = useCallback(() => latestPlayerViewPort.current, []);

    useEffect(() => {
        return subscribeToUpdates(viewPort => {
            latestPlayerViewPort.current = viewPort;
            setPlayerViewPort(viewPort);
        });
    }, [subscribeToUpdates]);

    useEffect(() => () => cancelScheduledUpdates(), [cancelScheduledUpdates]);

    return useMemo(() => ({
        playerViewPort,
        getPlayerViewPort,
        scheduleViewPortUpdate,
        persistViewPort,
        cancelScheduledUpdates,
    }), [
        cancelScheduledUpdates,
        getPlayerViewPort,
        persistViewPort,
        playerViewPort,
        scheduleViewPortUpdate,
    ]);
}
